// AAL-86 MyServices Copilot: Form Contract of the Service Designer.
//
// The contract describes every field of the Service Designer the Copilot may see or write.
// `buildFormContract` filters it by the Service Type template and populates it with the
// current values of a Service in the Service locale. The populated contract is the request
// body of the agent service (see README.md in this folder).
//
// Vocabulary follows GLOSSARY.md: Service (entity `product` in the code), Service Type,
// Form Contract, Proposal.

import { PriceType } from '@/constants'
import countries from '@/json/countries.json'

// ---------------------------------------------------------------------------------------
// Contract definition types
// ---------------------------------------------------------------------------------------

export type FieldType =
	| 'text'
	| 'richText'
	| 'number'
	| 'integer'
	| 'boolean'
	| 'enum'
	| 'money'
	| 'date'
	| 'time'
	| 'entry'
	| 'list'

export type FieldConstraints = {
	required?: boolean
	min?: number
	max?: number
	maxLength?: number
	allowedValues?: unknown[]
	/** Hint for `date` and `time` fields */
	format?: string
}

export type FieldDefinition = {
	/** Field key inside its section (or inside its parent list item) */
	key: string
	/**
	 * Entity path. Localized texts end with `{locale}`, replaced by the Service locale at build
	 * time. Inside a `list` field the path is relative to one item.
	 */
	path: string
	type: FieldType
	/** i18n key of the label shown on the page */
	label: string
	/** One sentence on what the field means, for the Copilot */
	description: string
	writable: boolean
	/** `append` for list fields where the Copilot may only add items (images) */
	writeMode?: 'set' | 'append'
	constraints?: FieldConstraints
	/** Service Type template key when it differs from `key`; `null` when the template never gates the field */
	templateKey?: string | null
	/** Sub-fields of one item of a `list` field */
	items?: FieldDefinition[]
	/**
	 * Writable only when the page lets the current user change the field (role, Client feature,
	 * impersonation): the page adapter names such fields in `BuildOptions.writableFields`.
	 */
	pageGated?: boolean
	/**
	 * Entry lists whose items wrap the entry the user picks (a client assignment wraps its Client, a
	 * sales channel assignment its sales channel): path inside one item to that entry. The value then
	 * lists the wrapped entries as `{ id, title }`.
	 */
	itemEntryPath?: string
	/**
	 * Fields on the current client assignment (`{clientAssignment}` in the path) when the Service has no
	 * assignment for the current Client yet (a new Service): the path the page keeps the value at instead,
	 * or nothing, and the field is read-only then.
	 */
	withoutClientAssignment?: string
}

export type SectionDefinition = {
	/** Service Type template key of the section (the same name the Service Designer uses in `hasSection`) */
	key: string
	label: string
	description: string
	fields: FieldDefinition[]
}

// ---------------------------------------------------------------------------------------
// Populated contract types (the agent service request body)
// ---------------------------------------------------------------------------------------

export type EntrySummary = { id: string, title?: string }

type BuildHint = 'templateKey' | 'pageGated' | 'itemEntryPath' | 'withoutClientAssignment'

export type PopulatedField = Omit<FieldDefinition, BuildHint> & {
	/**
	 * Current value in the Service locale; list fields hold one object per item, keyed by sub-field key.
	 * Items of an `itemEntryPath` list are `{ id, title }`, plus `locked: true` when the page does not
	 * let the user remove the item.
	 */
	value: unknown
}

export type PopulatedSection = {
	key: string
	label: string
	description: string
	fields: PopulatedField[]
}

export type PopulatedFormContract = {
	page: 'serviceDesigner'
	serviceLocale: string
	service: { id: string | null, isNew: boolean }
	sections: PopulatedSection[]
}

// ---------------------------------------------------------------------------------------
// Proposal types (produced by the agent service, consumed by the apply step of ticket 03)
// ---------------------------------------------------------------------------------------

type OperationBase = {
	/** Absolute entity path as it appears in the populated contract, list items addressed by index */
	path: string
	reason: string
	/** 0..1 */
	confidence: number
}

export type SetOperation = OperationBase & { op: 'set', value: unknown }
export type AppendOperation = OperationBase & { op: 'append', item: unknown }
export type RemoveOperation = OperationBase & { op: 'remove', index: number }
export type ProposalOperation = SetOperation | AppendOperation | RemoveOperation

export type Proposal = {
	message: string
	question?: string
	operations: ProposalOperation[]
}

// ---------------------------------------------------------------------------------------
// Service Designer contract
// ---------------------------------------------------------------------------------------

const LOCALE = '{locale}'

const text = (key: string, label: string, description: string, writable = true, constraints?: FieldConstraints, extra: Partial<FieldDefinition> = {}): FieldDefinition =>
	({ key, path: `fields.${key}.${LOCALE}`, type: 'text', label, description, writable, constraints, ...extra })

const de = (key: string, type: FieldType, label: string, description: string, writable: boolean, extra: Partial<FieldDefinition> = {}): FieldDefinition =>
	({ key, path: `fields.${key}.de`, type, label, description, writable, ...extra })

const entryItem = (key: string, path: string, label: string, description: string, writable: boolean, extra: Partial<FieldDefinition> = {}): FieldDefinition =>
	({ key, path, type: 'entry', label, description, writable, ...extra })

const ticketOptionFields: FieldDefinition[] = [
	entryItem('ticketType', 'fields.ticketType.de', 'ticketType', 'Who the ticket is for (adult, child, senior). Choose from the allowed values.', true, { constraints: { required: true } }),
	entryItem('reduction', 'fields.reduction.de', 'reduction', 'Price reduction category of the ticket (none, student, family). Choose from the allowed values.', true, { templateKey: 'ticketCategory' }),
	{ key: 'price', path: 'fields.price.de', type: 'money', label: 'price', description: 'Price of one ticket in the client currency.', writable: true, constraints: { required: true, min: 0 } },
	{ key: 'fromPriceOption', path: 'fields.fromPriceOption.de', type: 'boolean', label: 'fromPrice', description: 'Show the price as "from" price.', writable: true, templateKey: 'fromPrice' },
	{ key: 'priceType', path: 'fields.priceType.de', type: 'enum', label: 'priceType', description: 'Individual (fit), group or generic price.', writable: true, constraints: { allowedValues: Object.values(PriceType) } },
	{ key: 'minimumTickets', path: 'fields.minimumTickets.de', type: 'integer', label: 'minimumTickets', description: 'Minimum number of tickets per order.', writable: true, constraints: { min: 1 } },
	{ key: 'maximumTickets', path: 'fields.maximumTickets.de', type: 'integer', label: 'maximumTickets', description: 'Maximum number of tickets per order.', writable: true, constraints: { min: 1 } },
	{ key: 'contingentQuantity', path: 'fields.contingentQuantity.de', type: 'integer', label: 'contingentQuantity', description: 'How many contingent units one ticket consumes.', writable: true, constraints: { min: 1 } },
	{ key: 'ticketPersonalisations', path: 'fields.ticketPersonalisations.de', type: 'list', label: 'ticketPersonalisations', description: 'Personal data collected per ticket.', writable: false, templateKey: null },
	{ key: 'externalVariantKey', path: 'fields.externalVariantKey', type: 'text', label: 'externalVariantKey', description: 'Key of the ticket option in the external system.', writable: false, templateKey: null },
	{ key: 'ticketExternalSystemAssignment', path: 'fields.ticketExternalSystemAssignment', type: 'entry', label: 'ticketExternalSystemAssignment', description: 'External system the ticket option is sold through.', writable: false },
]

const contingentFields: FieldDefinition[] = [
	{ key: 'minimumContingent', path: 'fields.minimumContingent.de', type: 'integer', label: 'minimumContingent', description: 'Minimum bookable units per time slot.', writable: true, constraints: { min: 1 } },
	{ key: 'maximumContingent', path: 'fields.maximumContingent.de', type: 'integer', label: 'maximumContingent', description: 'Maximum bookable units per time slot.', writable: true, constraints: { min: 1 } },
	{ key: 'timeSlot', path: 'fields.timeSlot.de', type: 'time', label: 'timeSlot', description: 'Start time of the slot, empty for the whole day.', writable: true, constraints: { format: 'HH:mm' } },
]

const availabilityFields: FieldDefinition[] = [
	{ key: 'validFromDate', path: 'fields.validFromDate.de', type: 'date', label: 'validFromDate', description: 'First day the Service can be booked for.', writable: true, constraints: { required: true, format: 'YYYY-MM-DD' } },
	{ key: 'validToDate', path: 'fields.validToDate.de', type: 'date', label: 'validToDate', description: 'Last day the Service can be booked for.', writable: true, constraints: { required: true, format: 'YYYY-MM-DD' } },
	{ key: 'exceptions', path: 'fields.exceptions.de', type: 'list', label: 'exceptions', description: 'Weekdays (English names) on which the availability applies.', writable: true, constraints: { allowedValues: [ 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday' ] } },
	{ key: 'productContingents', path: 'fields.productContingents.de', type: 'list', label: 'productContingents', description: 'Time slots and their bookable quantity.', writable: true, items: contingentFields },
]

const seasonalPriceFields: FieldDefinition[] = [
	{ key: 'ticketType', path: 'ticketType', type: 'text', label: 'ticketType', description: 'Id of the ticket type the price applies to.', writable: true },
	{ key: 'ticketCategory', path: 'ticketCategory', type: 'text', label: 'reduction', description: 'Id of the reduction the price applies to.', writable: true },
	{ key: 'priceType', path: 'priceType', type: 'enum', label: 'priceType', description: 'Price type, only for trade hub clients.', writable: true, constraints: { allowedValues: Object.values(PriceType) } },
	{ key: 'price', path: 'price', type: 'money', label: 'price', description: 'Price in the client currency.', writable: true, constraints: { min: 0 } },
]

const seasonFields: FieldDefinition[] = [
	{ key: 'id', path: 'id', type: 'number', label: 'id', description: 'Season id, unique inside the Service.', writable: false },
	{ key: 'name', path: 'name', type: 'text', label: 'name', description: 'Season name shown in the price matrix.', writable: true, constraints: { required: true } },
	{ key: 'color', path: 'color', type: 'text', label: 'color', description: 'Hex colour of the season in the calendar.', writable: true },
	{ key: 'start', path: 'start', type: 'date', label: 'start', description: 'First day of the season.', writable: true, constraints: { format: 'YYYY-MM-DD' } },
	{ key: 'end', path: 'end', type: 'date', label: 'end', description: 'Last day of the season.', writable: true, constraints: { format: 'YYYY-MM-DD' } },
	{ key: 'weekdays', path: 'weekdays', type: 'list', label: 'weekdays', description: 'Weekdays of the season as numbers, 1 = Monday to 7 = Sunday.', writable: true, constraints: { allowedValues: [ 1, 2, 3, 4, 5, 6, 7 ] } },
	{ key: 'prices', path: 'prices', type: 'list', label: 'prices', description: 'One price per ticket type and reduction.', writable: true, items: seasonalPriceFields },
]

// Ticket 20: the contact details of the first contact info group, one item per contact info. The page shows
// Phone, E-Mail, Website and Address (ContactInfo.vue); an item uses `contactDetail`, the address item the
// address parts. The country is stored as the name object of countries.json; the page adapter turns the code
// into it.
const CONTACT_TYPES = [ 'Phone', 'E-Mail', 'Website', 'Address' ]
const ADDRESS = 'fields.contactAddresses.de.0.fields'
const contactInfoFields: FieldDefinition[] = [
	{ key: 'type', path: 'fields.contactInfoType.de.fields.type.de', type: 'enum', label: 'type', description: 'Kind of contact info. One item per kind.', writable: true, constraints: { required: true, allowedValues: CONTACT_TYPES } },
	{ key: 'contactDetail', path: 'fields.contactDetail.de', type: 'text', label: 'contactDetail', description: 'Phone number (digits, spaces and +, e.g. +41 27 123 45 67), e-mail address or website URL; empty for the address.', writable: true },
	{ key: 'streetAddress', path: `${ADDRESS}.streetAddress.de`, type: 'text', label: 'placeHolderAddress', description: 'Street and number, address item only.', writable: true },
	{ key: 'zipCode', path: `${ADDRESS}.zipCode.de`, type: 'text', label: 'placeHolderPostCode', description: 'Postal code, address item only.', writable: true },
	{ key: 'city', path: `${ADDRESS}.city.de`, type: 'text', label: 'placeHolderCityTown', description: 'City, address item only.', writable: true },
	{ key: 'country', path: `${ADDRESS}.country`, type: 'enum', label: 'placeHolderCountry', description: 'Country as ISO 3166-1 alpha-2 code (CH, AT, DE, ...), address item only; the current value shows the country name per locale.', writable: true, constraints: { allowedValues: countries.map(country => country.code) } },
]

// Ticket 20: one validity period of business hours with its weekly opening times (BusinessHoursNew.vue).
// A day with one empty time ({ openTime: '', closeTime: '' }) is closed, which is what the page skeleton holds.
const openingTimeFields: FieldDefinition[] = [
	{ key: 'openTime', path: 'openTime', type: 'time', label: 'openFrom', description: 'Opening time.', writable: true, constraints: { required: true, format: 'HH:mm' } },
	{ key: 'closeTime', path: 'closeTime', type: 'time', label: 'openTo', description: 'Closing time, after the opening time.', writable: true, constraints: { required: true, format: 'HH:mm' } },
]
// A closing day or special opening day (BusinessHoursNew.vue `exceptions`, shown in the calendar). The entity
// holds `{ dates, closed, times }`; the page writes one date per exception, so the contract does too.
const businessHoursExceptionFields: FieldDefinition[] = [
	{ key: 'date', path: 'dates.0', type: 'date', label: 'date', description: 'The day the exception applies to, inside the period.', writable: true, constraints: { required: true, format: 'YYYY-MM-DD' } },
	{ key: 'closed', path: 'closed', type: 'boolean', label: 'closed', description: 'True: closed all day. False: open with the times given.', writable: true, constraints: { required: true } },
	{ key: 'times', path: 'times', type: 'list', label: 'times', description: 'Opening times of that day when it is not closed; empty when closed.', writable: true, items: openingTimeFields },
]
/** BusinessHoursNew: the days of a period's weekly opening times; api1 saves a period only with a time on a weekday */
export const BUSINESS_WEEKDAYS = [ 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday' ]
export const BUSINESS_DAYS = [ ...BUSINESS_WEEKDAYS, 'holidays' ]
const businessHoursFields: FieldDefinition[] = [
	{ key: 'validFromDate', path: 'fields.validFromDate.de', type: 'date', label: 'dateFrom', description: 'First day of the period.', writable: true, constraints: { required: true, format: 'YYYY-MM-DD' } },
	{ key: 'validToDate', path: 'fields.validToDate.de', type: 'date', label: 'dateTo', description: 'Last day of the period, after the first day. Periods must not overlap.', writable: true, constraints: { required: true, format: 'YYYY-MM-DD' } },
	...BUSINESS_DAYS.map((day): FieldDefinition => ({
		key: day, path: `fields.businessTimes.de.${day}.times`, type: 'list', label: day, writable: true, items: openingTimeFields,
		description: day === 'holidays' ? 'Opening times on public holidays; leave out when closed.' : `Opening times on ${day}, one item per opening (two for a lunch break); leave out when closed.`,
	})),
	{ key: 'exceptions', path: 'fields.exceptions.de', type: 'list', label: 'exceptions', writable: true, items: businessHoursExceptionFields,
		description: 'Single dates inside the period that differ from the weekly times: closing days (closed true) or days with special opening times (closed false and times). One item per date.' },
]

export const SERVICE_DESIGNER_CONTRACT: SectionDefinition[] = [
	{
		key: 'publicInfo', label: 'publicInfo', description: 'Texts shown to customers in the shop.',
		fields: [
			text('title', 'Service_title', 'Name of the Service.', true, { required: true, maxLength: 255 }),
			text('shortDescription', 'Service_shortDescription', 'Teaser shown in listings, plain text.', true, { required: true, maxLength: 255 }),
			text('longDescription', 'Service_longDescription', 'Full description shown on the Service page.', true, { required: true }, { type: 'richText' }),
			text('importantInformation', 'Service_importantInformation', 'What the customer must know before booking (meeting time, equipment, conditions).', true, undefined, { type: 'richText', templateKey: 'importantInfo' }),
		],
	},
	{
		key: 'contactInfo', label: 'contactInfo', description: 'How customers reach the Service Provider for this Service.',
		fields: [
			text('meetingPoint', 'Service_meetingPoint', 'Where customers meet for the Service.', true),
			de('location', 'entry', 'location', 'Geo coordinates {lat, lon} of the Service.', false),
			de('copyContactInfos', 'boolean', 'copyContactInfo', 'true: the Service uses the contact data of the Service Provider profile. Set false when the input gives the Service its own phone, e-mail, website or address.', true, { templateKey: 'copyContactInfo' }),
			{ key: 'contactInfos', path: 'fields.contactInfoGroups.de.0.fields.contactInfos.de', type: 'list', label: 'contactInfoTitle', description: 'Own contact data of the Service, used when copyContactInfos is false; shown empty while it is true. The address needs street, postal code, city and country.', writable: true, templateKey: null, items: contactInfoFields },
			de('notificationEmails', 'list', 'notificationEmails', 'Addresses that receive booking notifications.', false),
		],
	},
	{
		key: 'generalInfo', label: 'generalInfo', description: 'Images, duration and additional structured information.',
		fields: [
			de('images', 'list', 'images', 'Images of the Service, media entries of the Service Provider. New images may only be appended.', true, { writeMode: 'append', constraints: { required: true } }),
			de('averageDurationOfStayInMinutes', 'integer', 'averageDurationOfStay', 'How long the Service takes, in minutes.', true, { templateKey: 'averageDurationOfStay', constraints: { min: 0 } }),
			de('faqGroup', 'entry', 'faqGroup', 'FAQ group shown with the Service. Choose from the allowed values.', true),
			de('infos', 'list', 'infos', 'Structured info blocks (id, infoTypeId, title, description per locale).', true),
			de('ctaButtons', 'list', 'ctaButtons', 'Call-to-action buttons (label, actionType, value).', false, { templateKey: null }),
			de('topProduct', 'boolean', 'topProduct', 'Operator flag: highlighted as top Service.', false),
			de('freeProduct', 'boolean', 'freeProduct', 'Operator flag: listed for free.', false),
			de('paidAd', 'boolean', 'paidAd', 'Operator flag: paid advertisement.', false),
		],
	},
	{
		key: 'businessInfo', label: 'businessInfo', description: 'Opening hours of the Service. The page offers three options: no business hours (copyBusinessHours false, businessHours empty), copy from the Service Provider profile (copyBusinessHours true), own business hours (copyBusinessHours false, businessHours filled).',
		fields: [
			de('copyBusinessHours', 'boolean', 'copyBusinessHours', 'true: the Service uses the opening hours of the Service Provider profile. Set false when the input gives the Service its own opening hours, or says it has none.', true),
			de('businessHours', 'list', 'businessHours', 'Own opening hours of the Service, used when copyBusinessHours is false; shown empty while it is true or while it holds only the page skeleton without times.', true, { items: businessHoursFields }),
		],
	},
	{
		key: 'categoryInfo', label: 'categoryInfo', description: 'Categories the Service is listed under on the client sales channels.',
		fields: [
			{ key: 'productCategories', path: `fields.clientAssignments.de.{clientAssignment}.fields.productCategories.de`, type: 'list', label: 'productCategories', description: 'Categories of the current client. Choose from the allowed values.', writable: true, constraints: { required: true }, withoutClientAssignment: 'fields.productCategories.de' },
		],
	},
	{
		key: 'customerSegmentTagsInfo', label: 'customerSegmentTags', description: 'Customer segments the Service is aimed at.',
		fields: [
			{ key: 'customerSegmentTags', path: `fields.clientAssignments.de.{clientAssignment}.fields.customerSegmentTags.de.values`, type: 'list', label: 'customerSegmentTags', description: 'Ids of the customer segment tags of the current client. Choose from the allowed values.', writable: true },
		],
	},
	{
		key: 'ticketInfo', label: 'ticketInfo', description: 'Ticket options the customer can buy.',
		fields: [
			{ key: 'ticketOptions', path: 'fields.ticket.de.fields.ticketOptions.de', type: 'list', label: 'tickets', description: 'One option per ticket type and reduction with its price.', writable: true, templateKey: 'tickets', constraints: { required: true }, items: ticketOptionFields },
			de('taxClass', 'entry', 'taxClass', 'VAT class of the Service. Choose from the allowed values.', true, { constraints: { required: true } }),
		],
	},
	{
		key: 'availabilityInfo', label: 'availabilityInfo', description: 'When the Service can be booked.',
		fields: [
			de('requestOnly', 'boolean', 'requestOnly', 'Bookings are requests confirmed by the Service Provider instead of instant purchases.', true),
			de('productAvailabilities', 'list', 'productAvailabilities', 'Date ranges with weekdays and time slots.', true, { constraints: { required: true }, items: availabilityFields }),
		],
	},
	{
		key: 'leadTimeInfo', label: 'publishingAndLeadtime', description: 'How far ahead a booking must be made and from when the Service is advertised.',
		fields: [
			de('leadTimeMonths', 'integer', 'leadTimeMonths', 'Lead time, months part.', true, { templateKey: 'leadTime', constraints: { min: 0 } }),
			de('leadTimeWeeks', 'integer', 'leadTimeWeeks', 'Lead time, weeks part.', true, { templateKey: 'leadTime', constraints: { min: 0 } }),
			de('leadTimeDays', 'integer', 'leadTimeDays', 'Lead time, days part.', true, { templateKey: 'leadTime', constraints: { min: 0 } }),
			de('leadTimeHours', 'integer', 'leadTimeHours', 'Lead time, hours part.', true, { templateKey: 'leadTime', constraints: { min: 0 } }),
			de('startAdvertisingDate', 'date', 'startAdvertisingDate', 'First day the Service is shown in the shop.', true, { constraints: { format: 'YYYY-MM-DD' } }),
		],
	},
	{
		key: 'seasonalPricing', label: 'seasonalPricing', description: 'Seasons with their own prices per ticket option.',
		fields: [
			de('seasonalPricing', 'list', 'seasonalPricing', 'Seasons (date range or explicit dates, weekdays) with prices.', true, { templateKey: null, items: seasonFields }),
		],
	},
	{
		key: 'ticketSettingsInfo', label: 'ticketSettingsInfo', description: 'Personalisation of tickets.',
		fields: [
			de('personalisationType', 'enum', 'personalisationType', 'Which personal data is collected per ticket.', false),
		],
	},
	{
		key: 'salesChannelsInfo', label: 'salesChannelsInfo', description: 'Clients and sales channels the Service is assigned to.',
		fields: [
			{ key: 'clientAssignments', path: 'fields.clientAssignments.de', type: 'list', label: 'clientAssignments', description: 'Clients the Service is assigned to, one client assignment each. Append the id of an allowed Client to assign the Service to it; removing a Client takes the Service offline there. Locked items cannot be removed.', writable: false, pageGated: true, itemEntryPath: 'fields.client.de' },
			{ key: 'salesChannels', path: 'fields.clientAssignments.de.{clientAssignment}.fields.salesChannelAssignments.de', type: 'list', label: 'salesChannels', description: 'Sales channels of the current Client the Service is sold on. Append the id of an allowed sales channel; locked items are mandatory channels and cannot be removed.', writable: false, pageGated: true, itemEntryPath: 'fields.salesChannel.de', templateKey: 'clientAssignments' },
			de('ticketLevelChannelingEnabled', 'boolean', 'ticketAssignment', 'Sales channels are assigned per ticket option instead of to the whole Service; the Service-wide sales channels are then not used.', false, { templateKey: null }),
			de('vouchers', 'list', 'vouchers', 'Voucher texts of the Service.', false, { templateKey: null }),
		],
	},
]

// ---------------------------------------------------------------------------------------
// Builder
// ---------------------------------------------------------------------------------------

export type ServiceTypeTemplate = Record<string, Record<string, unknown>> | null | undefined

export type BuildOptions = {
	/** Id of the current Client, used to find its client assignment for categories and tags */
	clientId?: string
	/** Allowed values for entry and list fields that come from Client or Service Provider data, keyed by field key */
	choices?: Record<string, EntrySummary[]>
	/** Sections the page hides for reasons outside the template (feature flags, roles) */
	excludeSections?: string[]
	/** Keys of the `pageGated` fields the page lets the current user change; every other gated field is read-only */
	writableFields?: string[]
	/** Ids of wrapped entries whose item the page does not let the user remove, keyed by field key */
	lockedItems?: Record<string, string[]>
	/**
	 * True when a list field holds nothing of the Service's own: a copy of the Service Provider profile or an
	 * untouched page skeleton. Such a list is shown empty; the apply step treats it as empty with the same
	 * predicate (ApplyOptions.isPlaceholderList).
	 */
	isPlaceholderList?: PlaceholderListPredicate
}

export type PlaceholderListPredicate = (field: Pick<FieldDefinition, 'key'>, value: unknown, entity: any) => boolean

export function buildFormContract(template: ServiceTypeTemplate, service: any, serviceLocale: string, options: BuildOptions = {}): PopulatedFormContract {
	const clientAssignmentIndex = findClientAssignmentIndex(service, options.clientId)
	const sections = SERVICE_DESIGNER_CONTRACT
		.filter(section => hasSection(template, section.key) && !options.excludeSections?.includes(section.key))
		.map(section => ({
			key: section.key,
			label: section.label,
			description: section.description,
			fields: section.fields
				.filter(field => hasField(template, section.key, field))
				.map(field => populateField(field, service, serviceLocale, clientAssignmentIndex, options)),
		}))
	return {
		page: 'serviceDesigner',
		serviceLocale,
		service: { id: service?.sys?.id || null, isNew: !service?.sys?.id },
		sections,
	}
}

// Mirrors Common.hasField / ServiceDesigner.hasSection: a Service without a template shows everything.
function hasSection(template: ServiceTypeTemplate, key: string): boolean {
	if (!template) return true
	return !!template[key]
}

function hasField(template: ServiceTypeTemplate, sectionKey: string, field: FieldDefinition): boolean {
	if (!template || field.templateKey === null) return true
	return !!template[sectionKey]?.[field.templateKey ?? field.key]
}

function populateField(field: FieldDefinition, service: any, locale: string, clientAssignmentIndex: number, options: BuildOptions): PopulatedField {
	const unassigned = clientAssignmentIndex < 0 && field.path.includes('{clientAssignment}')
	const definition = resolveDefinition(field, locale, Math.max(clientAssignmentIndex, 0), options.choices)
	if (field.pageGated) definition.writable = options.writableFields?.includes(field.key) === true
	if (unassigned) {
		// writing at index 0 would plant a client assignment without a Client (or edit another Client's)
		if (!field.withoutClientAssignment) return { ...definition, writable: false, value: field.type === 'list' ? [] : undefined }
		definition.path = resolvePath(field.withoutClientAssignment, locale, 0)
	}
	const value = readPath(service, definition.path)
	if (field.type === 'list' && options.isPlaceholderList?.(field, value, service)) return { ...definition, value: [] }
	if (field.itemEntryPath) return { ...definition, value: shapeWrappedEntries(field.itemEntryPath, value, locale, options.lockedItems?.[field.key]) }
	return { ...definition, value: shapeValue(field, value, locale) }
}

// The entries a list's items wrap, as { id, title }; the item's own title stands in for an unresolved link.
function shapeWrappedEntries(entryPath: string, value: unknown, locale: string, locked: string[] = []): unknown {
	if (!Array.isArray(value)) return value
	return value.map(item => {
		const entry = readPath(item, entryPath)
		if (!isEntry(entry)) return null
		const summary: EntrySummary & { locked?: true } = summarizeEntry(entry, locale)
		const ownTitle = item?.fields?.title?.[locale] ?? item?.fields?.title?.de
		if (summary.title === undefined && ownTitle !== undefined) summary.title = ownTitle
		if (locked.includes(summary.id)) summary.locked = true
		return summary
	})
}

// Resolves placeholders in the path, merges the allowed values handed in as choices and drops the
// template key, recursively for list item sub-fields.
function resolveDefinition(field: FieldDefinition, locale: string, clientAssignmentIndex: number, choices?: Record<string, EntrySummary[]>): Omit<FieldDefinition, BuildHint> {
	const { templateKey, pageGated, itemEntryPath, withoutClientAssignment, items, constraints, ...rest } = field
	const resolved: Omit<FieldDefinition, BuildHint> = { ...rest, path: resolvePath(field.path, locale, clientAssignmentIndex) }
	const allowed = choices?.[field.key]
	if (allowed) resolved.constraints = { ...constraints, allowedValues: allowed }
	else if (constraints) resolved.constraints = constraints
	if (items) resolved.items = items.map(item => resolveDefinition(item, locale, clientAssignmentIndex, choices))
	return resolved
}

// Entries are reduced to {id, title}; list items are flattened to their sub-fields. Everything else is
// passed through as the entity holds it.
function shapeValue(field: FieldDefinition, value: unknown, locale: string): unknown {
	if (value === null || value === undefined) return value
	if (field.type === 'entry') return isEntry(value) ? summarizeEntry(value, locale) : value
	if (field.type === 'list' && Array.isArray(value)) {
		if (field.items) {
			const items = field.items
			return value.map(item => Object.fromEntries(items.map(sub => [ sub.key, shapeValue(sub, readPath(item, sub.path), locale) ])))
		}
		return value.map(item => (isEntry(item) ? summarizeEntry(item, locale) : item))
	}
	return value
}

function isEntry(value: unknown): value is { sys: { id: string }, fields?: any } {
	return typeof value === 'object' && value !== null && typeof (value as any).sys?.id === 'string'
}

function summarizeEntry(entry: { sys: { id: string }, fields?: any }, locale: string): EntrySummary {
	const title = entry.fields?.title?.[locale] ?? entry.fields?.title?.de ?? entry.fields?.name?.[locale] ?? entry.fields?.name?.de
	return title === undefined ? { id: entry.sys.id } : { id: entry.sys.id, title }
}

function resolvePath(path: string, locale: string, clientAssignmentIndex: number): string {
	return path.replace(LOCALE, locale).replace('{clientAssignment}', String(clientAssignmentIndex))
}

// -1 when the Service has no assignment for the current Client (a new Service): the page then keeps the
// categories in `fields.productCategories` (ServiceDesigner.updateProductFromChildren), see
// FieldDefinition.withoutClientAssignment.
function findClientAssignmentIndex(service: any, clientId?: string): number {
	const assignments: any[] = service?.fields?.clientAssignments?.de ?? []
	return assignments.findIndex(ca => ca?.fields?.client?.de?.sys?.id === clientId)
}

function readPath(root: unknown, path: string): unknown {
	return path.split('.').reduce<any>((node, segment) => node?.[segment], root)
}
